"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useAuth } from "@tillix/tx-core/react";
import { Button } from "@tillix/ui-atoms/button";
import { Form } from "@tillix/ui-atoms/form";
import { FormRootError } from "@tillix/ui-atoms/form-root-error";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import OrSeparator from "@/components/tenant/howard-county/components/OrSeparator";
import { useT } from "@/features/i18n/hooks/use-t";
import MagicLinkButton from "../atoms/MagicLinkButton";
import EmailInput from "../fields/EmailInput";
import PasswordInput from "../fields/PasswordInput";
import {
  defaultSignUpFormValues,
  SignUpFormSchema,
  type SignUpFormValues,
} from "../schemas/sign-up";
import { sendMagicLink } from "../utils/send-magic-link";
import type { TenantAuthConfig } from "../config/tenant-auth";

interface SignUpFormProps {
  authConfig: TenantAuthConfig;
}

export default function SignUpForm({ authConfig }: SignUpFormProps) {
  const authStore = useAuth();
  const router = useRouter();
  const { t } = useT();
  const { t: tErrors } = useT("errors");

  const hasPassword = authConfig.allowedMethods.includes("password");
  const hasMagicLink = authConfig.allowedMethods.includes("magic-link");
  const hasMultipleMethods = authConfig.allowedMethods.length > 1;

  const [useMagicLink, setUseMagicLink] = useState(
    authConfig.defaultMethod === "magic-link",
  );

  const form = useForm<SignUpFormValues>({
    resolver: arktypeResolver(SignUpFormSchema),
    mode: "onChange",
    defaultValues: {
      ...defaultSignUpFormValues,
      password: authConfig.defaultMethod === "magic-link" ? undefined : "",
      confirmPassword:
        authConfig.defaultMethod === "magic-link" ? undefined : "",
    },
  });
  const { errors } = useFormState({ control: form.control });
  const rootError = errors.root?.message;

  const renderMagicLinkContent = () => {
    return (
      <>
        <p className="text-center text-muted-foreground text-sm">
          {t("auth.magicLinkDescriptionSignUp")}
        </p>
        <MagicLinkButton<SignUpFormValues> />
        <FormRootError keyPrefix="auth" rootError={rootError} />
      </>
    );
  };

  const renderPasswordContent = () => {
    return (
      <>
        <PasswordInput<SignUpFormValues> showForgotPasswordLink={false} />
        <PasswordInput<SignUpFormValues>
          name="confirmPassword"
          showForgotPasswordLink={false}
        />
        <FormRootError keyPrefix="auth" rootError={rootError} />
        <Button
          className="mt-2 bg-primary text-white uppercase hover:bg-primary/80"
          disabled={!form.formState.isValid || form.formState.isSubmitting}
          type="submit"
        >
          {form.formState.isSubmitting
            ? t("auth.signingUp")
            : t("application.continue")}
        </Button>
      </>
    );
  };

  const handleFormSubmit = async (values: SignUpFormValues) => {
    if (useMagicLink) {
      await sendMagicLink({
        authStore,
        form,
        email: values.email,
        mode: "sign-up",
        t,
      });
      return;
    }

    if (!values.password) {
      form.setError("root", { message: tErrors("auth.PASSWORD_REQUIRED") });
      return;
    }

    const result = await authStore.signUp({
      email: values.email,
      password: values.password,
      name: "",
    });

    if (result.authResult.error) {
      console.error("failed to sign up, error:", {
        code: result.authResult.error.code,
        message: result.authResult.error.message,
      });
      form.setError("root", {
        message:
          result.authResult.error.code ?? result.authResult.error.message,
      });
    } else {
      console.log("signed up successfully");
      router.refresh();
    }
  };

  const toggleMagicLinkMode = () => {
    form.clearErrors("root");
    const willUseMagicLink = !useMagicLink;
    setUseMagicLink(willUseMagicLink);
    if (willUseMagicLink) {
      form.setValue("password", undefined, { shouldValidate: true });
      form.setValue("confirmPassword", undefined, { shouldValidate: true });
    }
  };

  const toggleButtonText = useMagicLink
    ? t("auth.usePasswordSignUpInstead")
    : t("auth.useMagicLinkInstead");

  return (
    <FormProvider {...form}>
      <Form
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit(handleFormSubmit)}
      >
        <EmailInput<SignUpFormValues> />

        {useMagicLink ? renderMagicLinkContent() : renderPasswordContent()}

        {hasMultipleMethods && hasPassword && hasMagicLink && (
          <>
            <OrSeparator />
            <Button
              onClick={toggleMagicLinkMode}
              type="button"
              variant="outline"
            >
              {toggleButtonText}
            </Button>
          </>
        )}
      </Form>
    </FormProvider>
  );
}
